import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Layout, Typography, Table, Col } from "antd";
import { calculatePercentage, parseOptionalCount } from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const { Header, Content } = Layout;
const { Title, Text, Link } = Typography;

const GA_MEETING_EVIDENCE_SLUG = "c1.0-1.1";

function scrollToIndicator(slug) {
  document.querySelector(`[data-dpat-indicator="${slug}"]`)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function GeneralAssemblyEvidenceGuide() {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm" style={{ marginTop: "16px" }}>
      <Text>
        General Assembly meeting evidence — see{" "}
        <Link onClick={() => scrollToIndicator(GA_MEETING_EVIDENCE_SLUG)}>CI 1.1</Link>
      </Text>
    </div>
  );
}

const GeneralAssemblyDecision = forwardRef(({
  data,
  year,
  columns,
  decisionDeliveryData,
  serviceDeliveryDecisionColumns,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
  gaMeetingFulfillment,
}, ref) => {
  const [gaDecisionScore, setGaDecisionScore] = useState(0);
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(1);

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      data: savedAssessmentData.data ?? data,
      decisionDeliveryData: savedAssessmentData.decisionDeliveryData ?? decisionDeliveryData,
      gaDecisionScore: savedAssessmentData.gaDecisionScore ?? 0,
      score: savedAssessmentData.score ?? 0,
      maxScore: savedAssessmentData.maxScore ?? 1,
    };
  }, [isAssessmentStarted, savedAssessmentData, data, decisionDeliveryData]);

  const displayData = resolved?.data ?? data;
  const displayDecisionDelivery = resolved?.decisionDeliveryData ?? decisionDeliveryData;
  const displayGaScore = resolved?.gaDecisionScore ?? gaDecisionScore;
  const displayScore = resolved?.score ?? score;
  const displayMaxScore = resolved?.maxScore ?? maxScore;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI1",
      area: "Management Coordination – Implementation of Service Delivery Decisions",
      maxScore: displayMaxScore,
      data: displayData,
      decisionDeliveryData: displayDecisionDelivery,
      gaDecisionScore: displayGaScore,
      score: displayScore,
    }),
  }));

  const DPAT_SLUG = "sdi1.0-1.1";

  return (
    <Comment
      data={displayData}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi1.0-1.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />
          <IndicatorScoreSection>
            <MaxScoreDisplay score={displayMaxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 1.0-1.1 Actual Score" score={displayScore} />
          </IndicatorScoreSection>

          <GeneralAssemblyEvidenceGuide />

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of General Assembly Meetings Decisions</Title>

          {displayData && <Table columns={columns} dataSource={displayData} pagination={false} bordered
            summary={pageData => {
              let totalDecision = 0;
              let totalDelivered = 0;

              pageData.forEach(({ total, serviceDecision }) => {
                const totalCount = parseOptionalCount(total);
                const serviceCount = parseOptionalCount(serviceDecision) ?? 0;

                if (totalCount != null) {
                  totalDecision += totalCount;
                }
                totalDelivered += serviceCount;
              });

              const percentage = calculatePercentage(totalDelivered, totalDecision);
              const percentageDisplay = Number(percentage.toFixed(2));

              if (!isAssessmentStarted) {
                setGaDecisionScore(percentageDisplay);
                const meetsThreshold = percentageDisplay > 70;
                const meetingEvidenceComplete = isDpatScoreRelaxationExempt() || gaMeetingFulfillment === "Fulfilled";
                setScore(meetsThreshold && meetingEvidenceComplete ? 1 : 0);
              }

              return (
                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                  <Table.Summary.Cell>Total Decisions</Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <Text>{totalDecision}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <Text>{totalDelivered}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <Text>{percentageDisplay}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />}
          <Title level={5} style={{ marginTop: "20px" }}>Service Delivery Decisions</Title>
          {displayDecisionDelivery && <Table columns={serviceDeliveryDecisionColumns} dataSource={displayDecisionDelivery} pagination={false} bordered />}

          <Title level={5} style={{ marginTop: "20px" }}>Conclusion</Title>
          <Content>
            The decisions that were on improving service delivery was {`${displayGaScore}%`} of the total no. of decisions made at GA Meetings in {year}.
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default GeneralAssemblyDecision;